import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { onUnmounted, ref } from "vue";
import { useSiteWideHubManager } from "~/composables/useSiteWideHubManager";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { hubManager } from "~/utils/hubManager";

/**
 * Composable for managing real-time form response collaboration
 *
 * This composable provides functionality for:
 * 1. Tracking who is currently viewing a form response
 * 2. Receiving real-time updates when the form is modified by others
 * 3. Managing SignalR connections for form collaboration
 *
 * It uses SignalR to establish a real-time connection with the server
 * and join a group specific to the form response being viewed.
 */

/** User data from FormResponseEditors event */
export interface FormResponseEditorUser {
  connectionId: string;
  userName: string;
  joinedAt: string;
  isIdle: boolean;
}

export function useLiveFormResponseManager() {
  // Access user information for identifying the current user
  const myInfoStore = useMyInfoStore();
  const { isServerOffline } = useSiteWideHubManager();

  // Reactive state for tracking connection status and viewers
  const isConnected = ref(false);

  // Map of users viewing responses: { formGuid: [usernames] }
  const viewerResponseMap = ref<Record<string, string[]>>({});

  // Rich editor data for concurrency mode logic
  const formResponseEditors = ref<FormResponseEditorUser[]>([]);

  // SignalR connection instance
  let connection: HubConnection | null = null;

  // Store the current form response GUID for cleanup
  let responseGuid: string | null = null;

  // Store handler references for proper cleanup
  let viewerListHandler: ((data: Record<string, string[]>) => void) | null =
    null;
  let formUpdatedHandler: (() => void) | null = null;
  let editorsHandler:
    | ((data: { users: FormResponseEditorUser[] }) => void)
    | null = null;
  let controlRequestHandler: ((request: any) => void) | null = null;
  let controlResponseHandler: ((response: any) => void) | null = null;

  /**
   * Establishes a connection to the form response SignalR hub
   * and joins the group for the specified form response
   */
  const startConnection = async (formResponseGuid: string): Promise<void> => {
    // Validate input
    if (!formResponseGuid) {
      console.warn("Form response GUID is not provided.");
      return;
    }

    // Don't attempt SignalR connections when server is known to be offline.
    // The caller is responsible for re-calling startConnection once the server
    // comes back online (e.g., in response to polling recovery or ServerOnline event).
    if (isServerOffline.value) {
      return;
    }

    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // console.log(`Starting SignalR connection for form response: ${formResponseGuid} (attempt ${attempt}/${maxRetries})`);

        // Clean up any existing connection on retry
        if (attempt > 1 && connection) {
          try {
            if (viewerListHandler) {
              connection.off("ViewerListUpdated", viewerListHandler);
              viewerListHandler = null;
            }
            if (formUpdatedHandler) {
              connection.off("FormUpdated", formUpdatedHandler);
              formUpdatedHandler = null;
            }
            if (editorsHandler) {
              connection.off("FormResponseEditors", editorsHandler);
              editorsHandler = null;
            }
            if (controlRequestHandler) {
              connection.off("ControlRequestReceived", controlRequestHandler);
              controlRequestHandler = null;
            }
            if (controlResponseHandler) {
              connection.off("ControlRequestResponse", controlResponseHandler);
              controlResponseHandler = null;
            }
          } catch (cleanupError) {
            console.warn(
              "Error cleaning up handlers during retry:",
              cleanupError,
            );
          }
          connection = null;
        }

        // Create the connection
        connection = await hubManager.getHub("formResponse", "/formResponse");

        if (!connection) {
          throw new Error(
            "Failed to create SignalR connection - hubManager returned null",
          );
        }

        // console.log(`SignalR connection state: ${connection.state}`);

        // Set up event handlers first (only once per connection)
        if (!viewerListHandler) {
          // Create and register handler for viewer list updates
          viewerListHandler = (data: Record<string, string[]>) => {
            viewerResponseMap.value = data;
          };
          connection.on("ViewerListUpdated", viewerListHandler);
        }

        if (!editorsHandler) {
          editorsHandler = (data: { users: FormResponseEditorUser[] }) => {
            formResponseEditors.value = data.users;
          };
          connection.on("FormResponseEditors", editorsHandler);
        }

        // Verify connection is established
        if (connection.state !== HubConnectionState.Connected) {
          throw new Error(
            `SignalR connection is not in Connected state. Current state: ${connection.state}`,
          );
        }

        // console.log(`Joining form group for response: ${formResponseGuid}`);

        // Join the form group with timeout
        const joinTimeout = 10000; // 10 seconds
        const joinPromise = connection.invoke(
          "JoinFormGroup",
          formResponseGuid,
          myInfoStore.userInfo.loginName,
        );

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("JoinFormGroup timed out")),
            joinTimeout,
          );
        });

        await Promise.race([joinPromise, timeoutPromise]);

        // console.log("Successfully joined form group");

        // Update connection state
        isConnected.value = true;
        responseGuid = formResponseGuid;

        // Success - break out of retry loop
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (isServerOffline.value) {
          console.debug(
            `SignalR start error (attempt ${attempt}/${maxRetries}) - server is offline:`,
            lastError,
          );
        } else {
          console.error(
            `SignalR start error (attempt ${attempt}/${maxRetries}):`,
            lastError,
          );
        }

        // Clean up on error
        isConnected.value = false;
        responseGuid = null;

        // Clean up handlers if they were created
        if (viewerListHandler && connection) {
          try {
            connection.off("ViewerListUpdated", viewerListHandler);
          } catch (cleanupError) {
            console.warn(
              "Error cleaning up ViewerListUpdated handler:",
              cleanupError,
            );
          }
          viewerListHandler = null;
        }
        if (editorsHandler && connection) {
          try {
            connection.off("FormResponseEditors", editorsHandler);
          } catch {
            /* ignore */
          }
          editorsHandler = null;
        }

        // If this is not the last attempt, wait before retrying (skip retry if server is offline)
        if (attempt < maxRetries && !isServerOffline.value) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else if (isServerOffline.value) {
          break; // Don't retry when server is known to be offline
        }
      }
    }

    // If we get here, all retries failed
    if (!isServerOffline.value) {
      throw (
        lastError || new Error("SignalR connection failed after all retries")
      );
    }
  };

  /**
   * Registers a callback to be invoked when the form is updated by another user
   */
  const onFormUpdated = (callback: () => void): void => {
    // Clean up existing handler for this form instance
    if (formUpdatedHandler && connection) {
      connection.off("FormUpdated", formUpdatedHandler);
    }

    // Store and register the new handler
    formUpdatedHandler = callback;
    connection?.on("FormUpdated", formUpdatedHandler);
  };

  /**
   * Registers a callback for when this user receives a control request from another user
   */
  const onControlRequestReceived = (callback: (request: any) => void): void => {
    if (controlRequestHandler && connection) {
      connection.off("ControlRequestReceived", controlRequestHandler);
    }
    controlRequestHandler = callback;
    connection?.on("ControlRequestReceived", controlRequestHandler);
  };

  /**
   * Registers a callback for when this user receives a control request response
   */
  const onControlRequestResponse = (
    callback: (response: any) => void,
  ): void => {
    if (controlResponseHandler && connection) {
      connection.off("ControlRequestResponse", controlResponseHandler);
    }
    controlResponseHandler = callback;
    connection?.on("ControlRequestResponse", controlResponseHandler);
  };

  /**
   * Sends a request to the current editor to transfer edit control
   */
  const requestFormControl = async (
    formResponseGuid: string,
  ): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke("RequestFormControl", formResponseGuid);
    }
  };

  /**
   * Responds to a control request (approve or deny)
   */
  const respondToControlRequest = async (
    formResponseGuid: string,
    requestingConnectionId: string,
    approved: boolean,
  ): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke(
        "RespondToControlRequest",
        formResponseGuid,
        requestingConnectionId,
        approved,
      );
    }
  };

  /**
   * Reports the current user as idle for a form response
   */
  const reportIdle = async (formResponseGuid: string): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke("ReportIdle", formResponseGuid);
    }
  };

  /**
   * Reports the current user as active (no longer idle) for a form response
   */
  const reportActive = async (formResponseGuid: string): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke("ReportActive", formResponseGuid);
    }
  };

  /**
   * Leaves a form response group, notifying other users that you're no longer viewing it
   */
  const leaveFormGroup = async (formResponseGuid: string): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke(
        "LeaveFormGroup",
        formResponseGuid,
        myInfoStore.userInfo.loginName,
      );
    }
  };

  /**
   * Stops the SignalR connection and cleans up resources
   *
   * This function:
   * 1. Leaves the current form response group
   * 2. Cleans up event handlers for this form instance
   * 3. Updates the local viewer map to remove the current user
   */
  const stopConnection = async (): Promise<void> => {
    try {
      // console.log("Stopping SignalR connection...");

      if (
        connection &&
        connection.state === HubConnectionState.Connected &&
        responseGuid
      ) {
        // Leave the current form group
        // console.log(`Leaving form group for response: ${responseGuid}`);
        await leaveFormGroup(responseGuid);
      }

      // Clean up handlers for this form instance only
      if (viewerListHandler && connection) {
        try {
          connection.off("ViewerListUpdated", viewerListHandler);
          // console.log("Removed ViewerListUpdated handler");
        } catch (error) {
          console.warn("Error removing ViewerListUpdated handler:", error);
        }
        viewerListHandler = null;
      }

      if (formUpdatedHandler && connection) {
        try {
          connection.off("FormUpdated", formUpdatedHandler);
        } catch (error) {
          console.warn("Error removing FormUpdated handler:", error);
        }
        formUpdatedHandler = null;
      }

      if (editorsHandler && connection) {
        try {
          connection.off("FormResponseEditors", editorsHandler);
        } catch {
          /* ignore */
        }
        editorsHandler = null;
      }

      if (controlRequestHandler && connection) {
        try {
          connection.off("ControlRequestReceived", controlRequestHandler);
        } catch {
          /* ignore */
        }
        controlRequestHandler = null;
      }

      if (controlResponseHandler && connection) {
        try {
          connection.off("ControlRequestResponse", controlResponseHandler);
        } catch {
          /* ignore */
        }
        controlResponseHandler = null;
      }

      // Note: We don't stop the hub connection as other forms might be using it
      // The hubManager will handle connection lifecycle appropriately

      isConnected.value = false;

      // Remove current user from the viewer map
      const myName = myInfoStore.userInfo.loginName;
      const newMap = { ...viewerResponseMap.value };
      delete newMap[myName];
      viewerResponseMap.value = newMap;

      // console.log("SignalR connection stopped successfully");
    } catch (error) {
      console.error("Error stopping SignalR connection:", error);
      // Still clean up local state even if server operations fail
      isConnected.value = false;
      viewerListHandler = null;
      formUpdatedHandler = null;
      editorsHandler = null;
      controlRequestHandler = null;
      controlResponseHandler = null;
    } finally {
      // Reset the response GUID
      responseGuid = null;
    }
  };

  // Clean up the connection when the component is unmounted
  onUnmounted(async () => {
    await stopConnection();
  });

  // Return the public API of the composable
  return {
    isConnected,
    viewerResponseMap,
    formResponseEditors,
    stopConnection,
    startConnection,
    leaveFormGroup,
    onFormUpdated,
    onControlRequestReceived,
    onControlRequestResponse,
    requestFormControl,
    respondToControlRequest,
    reportIdle,
    reportActive,
  };
}
